/**
 * Meta (Facebook) browser Pixel — client-side funnel events.
 *
 * Complements the server-side Conversions API (server/lib/meta.js). The browser
 * pixel fires PageView + InitiateCheckout on the checkout page; the thank-you
 * page fires Purchase. The server fires Purchase via CAPI. Browser and CAPI
 * Purchase share event_id = orderName, so Meta deduplicates them into a single
 * conversion. Loading the pixel also drops a first-party `_fbp` cookie on our
 * domain, which improves CAPI match quality.
 *
 * Config-driven & per-shop, mirroring the server: the pixel ID is fetched from
 * /api/pixel-config?shop=<domain>. Shops without a configured pixel (e.g. the
 * kenvit-3m test store) get no pixel — every call here becomes a silent no-op.
 */

let pixelId = null;
let initStarted = false;

/** Standard fbevents.js loader. Defines window.fbq exactly once. */
function injectBase() {
  if (window.fbq) return;
  /* eslint-disable */
  !function (f, b, e, v, n, t, s) {
    if (f.fbq) return; n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0';
    n.queue = []; t = b.createElement(e); t.async = !0; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
}

/** Extract the trailing numeric id from a Shopify GID (catalog retailer_id). */
export function gidToId(gid) {
  const m = String(gid || '').match(/(\d+)\s*$/);
  return m ? m[1] : '';
}

/**
 * Initialise the pixel for a shop and fire PageView. Idempotent. Resolves to
 * `true` when a pixel is configured + active for the shop, `false` otherwise
 * (unconfigured shop or fetch failure → caller's events become no-ops).
 */
export async function initPixel(shop) {
  if (initStarted) return !!pixelId;
  initStarted = true;
  try {
    const res = await fetch(`/api/pixel-config?shop=${encodeURIComponent(shop || '')}`);
    const cfg = res.ok ? await res.json() : {};
    if (!cfg.metaPixelId) return false;
    pixelId = cfg.metaPixelId;
    injectBase();
    window.fbq('init', pixelId);
    window.fbq('track', 'PageView');
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Fire a standard event when the pixel is active. Passing `eventId` sets the
 * dedup key Meta uses to merge this with the matching server-side CAPI event.
 */
export function track(event, params = {}, eventId) {
  if (!pixelId || !window.fbq) return;
  if (eventId) window.fbq('track', event, params, { eventID: String(eventId) });
  else window.fbq('track', event, params);
}

/**
 * Build the Meta { contents, content_ids, content_type, num_items } block from
 * the checkout cart's lines. content id = numeric variant id (catalog
 * retailer_id), falling back to SKU.
 */
export function contentsFromCartLines(lines = []) {
  const contents = lines.map((l) => ({
    id: gidToId(l.variantId) || l.sku || '',
    quantity: l.qty,
    item_price: l.price,
  }));
  return {
    content_ids: contents.map((c) => c.id),
    contents,
    content_type: 'product',
    num_items: lines.reduce((a, l) => a + (l.qty || 0), 0),
  };
}
